import debounce from 'debounce-promise';
import { useEffect, useMemo, useState } from 'react';
import { CA_PROVINCES } from '../../utils/countries.ts';
import { GtmEvents } from '../../utils/gtm/gtm';

export type TAutocompletePrediction = {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
};

const usePlacesAutocompleteWithDetails = (input: string, countryCode: string, provinceCode?: string) => {
  const [predictions, setPredictions] = useState<TAutocompletePrediction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const filterByProvince = (results: TAutocompletePrediction[]): TAutocompletePrediction[] => {
    if (countryCode !== 'CA' || !provinceCode) return results;

    const canadaProvince = CA_PROVINCES.find(p => p.code === provinceCode);
    if (!canadaProvince) return results;

    return results.filter(prediction => {
      const description = prediction.description.toLowerCase();

      return description.includes(canadaProvince.code.toLowerCase()) ||
        description.includes(canadaProvince.name.toLowerCase());
    });
  };

  const fetchPredictions = (inputValue: string, countryCode?: string) => {
    setIsLoading(true);

    const timeStart = new Date().getTime();
    const autocompleteService = new google.maps.places.AutocompleteService();

    GtmEvents.logSearchAddressSent(inputValue, timeStart);

    return new Promise<void>((resolve, reject) => {
      autocompleteService.getPlacePredictions({
        input:                 inputValue,
        componentRestrictions: { country: countryCode || null },
        types:                 ['address'],
      }, (results, status) => {
        setIsLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const filteredResults = filterByProvince(results || []);

          GtmEvents.logSearchAddressReceived(inputValue, timeStart, status, filteredResults);
          setPredictions(filteredResults || []);
          resolve();
        } else {
          setPredictions([]);
          GtmEvents.logSearchAddressError(inputValue, status);
          setError(`Error fetching autocomplete suggestions: ${status}`);
          console.error(`Error fetching autocomplete suggestions: ${status}`);
          reject(status);
        }
      }).then(() => {
        console.info('Autocomplete predictions fetched');
      });
    });
  };

  const debouncedFetchPredictions = useMemo(() => {
    const debouncedFn = debounce(fetchPredictions, 1000);

    return async (inputValue: string, countryCode?: string) => {
      setIsLoading(true);

      try {
        return await debouncedFn(inputValue, countryCode);
      } finally {
        setIsLoading(false);
      }
    };
  }, []);

  useEffect(() => {
    if (!input) {
      setPredictions([]);

      return;
    }

    debouncedFetchPredictions(input, countryCode).catch(console.error);
  }, [input, countryCode, debouncedFetchPredictions]);

  const fetchDetailedAddress = async (
    prediction: TAutocompletePrediction,
  ): Promise<google.maps.GeocoderResult | null> => {
    const { place_id, structured_formatting: { main_text } } = prediction;

    GtmEvents.logSelectAddress(main_text);

    if (!(window && window.google) || !place_id) {
      console.error('Google Maps API not available or invalid placeId');

      return null;
    }

    const geocoder = new google.maps.Geocoder();

    try {
      const { results } = await geocoder.geocode({ placeId: place_id });

      if (results.length === 0) {
        console.error(`No places were returned for placeID: ${place_id}`);

        return null;
      }

      return results[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(`Error fetching detailed address: ${error.message}`);

      return null;
    }
  };

  return {
    predictions,
    fetchDetailedAddress,
    isLoading,
    error,
  };
};

export default usePlacesAutocompleteWithDetails;
